import '@/styles/index.css';

import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { createFrontendBeforeSendHandler } from '@mesa/core/utils/error-classification';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { scan } from 'react-scan';

import { api, queryClient } from './lib/clients';
import { createRouter } from './lib/router';

import { TooltipProvider } from '@/components/ui/tooltip';
import { CurrentOrgProvider } from '@/services/org';

if (import.meta.env.VITE_SCAN === 'true') {
  scan({ enabled: true });
}

if (import.meta.env.VITE_STAGE === 'production' || import.meta.env.VITE_STAGE === 'staging') {
  Sentry.init({
    dsn: 'https://971b5bdb47e9fe7dd47602e4caf3fb92@o4509358262714368.ingest.us.sentry.io/4509358269988864',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    environment: import.meta.env.VITE_STAGE,
    tracesSampleRate: 1.0,
    profilesSampleRate: 0.1,
    tracePropagationTargets: ['localhost', /^\//, /^https:\/\/api\.mesa\.dev/, /^https:\/\/app\.mesa\.dev\/api\//],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    replaysSessionSampleRate: 0, // Don't record normal sessions
    replaysOnErrorSampleRate: 1.0, // Always record sessions with errors
    beforeSend: createFrontendBeforeSendHandler(),
  });
}

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const router = createRouter(queryClient, api);

const App = () => {
  const { isSignedIn, isLoaded, getToken, userId } = useAuth();

  // Set Sentry user context when user is authenticated
  React.useEffect(() => {
    if (isSignedIn && userId) {
      Sentry.setUser({ id: userId });
    } else {
      Sentry.setUser(null);
    }
  }, [isSignedIn, userId]);

  if (!isLoaded) return null;
  return <RouterProvider router={router} context={{ isSignedIn, getToken, userId }} />;
};

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ClerkProvider
        publishableKey={CLERK_KEY}
        routerReplace={(to: string) => router.navigate({ to, replace: true })}
        routerPush={(to: string) => router.navigate({ to, replace: false })}
        afterSignOutUrl="/"
        signInForceRedirectUrl="/auth/callback"
        signUpForceRedirectUrl="/auth/callback"
      >
        <QueryClientProvider client={queryClient}>
          <CurrentOrgProvider>
            <TooltipProvider delayDuration={300} skipDelayDuration={100}>
              <App />
            </TooltipProvider>
          </CurrentOrgProvider>
          {/* Disabling the tanstack devtools because they're annoying overlay */}
          {/* <ReactQueryDevtools /> */}
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>
  );
}
